import * as core from './core.ts';
import * as common from './common.ts';
import {newWorker} from '../worker/core.ts'
import type { Logger, LogEvent, LogConfig } from '../../types/logging/core';
import { getLogger, logEvent } from '@interopio/gateway/logging/core';
import {deserializeError} from 'serialize-error';
import {sanitizerOf} from '../common/utilities.ts';
import {isAllowed} from '../domains/metrics/filters.ts';
import type { PublishCommand } from './publisher/types.ts';
import { IOGateway } from '../../gateway';

const log: Logger = getLogger('gateway.metrics.publisher');

function makeStatusUpdate(repoId: IOGateway.Metrics.Identity,
                          status: core.Status, metadata?: unknown): common.StatusUpdateEvent {
    const statusUpdate = {identity: repoId, status: {...status, 'updated-at': status.updated, 'expires-at': status.expires}};
    if (metadata) {
        statusUpdate['metadata'] = metadata;
    }
    return statusUpdate;
}

type LatestStatus = {
    status: Omit<core.Status, 'timestamp' | 'expires' | 'expires-at' | 'updated-at'>
    timestamp?: number,
    expires?: number,
    initial: boolean,
    stopped: boolean
}

class BasicRepository implements core.Repository {
    private running: boolean = true;
    private readonly latestStatus: LatestStatus = {
        initial: true,
        stopped: false,
        status: {state: 0, updated: Date.now(), description: 'Running'}
    };
    private heartbeatCleanup?: NodeJS.Timeout;
    private readonly metrics: Map<string, IOGateway.Metrics.Definition> = new Map();

    constructor(private readonly publisher: Publisher,
                private readonly filters: IOGateway.MetricFilters | undefined,
                private readonly repoId: IOGateway.Metrics.Identity,
                private readonly heartbeatInterval: number,
                private readonly metadata?: unknown) {
    }

    start() {
        log.info(`starting repository for ${JSON.stringify(this.repoId)} with heartbeat interval ${this.heartbeatInterval}ms`);
        const now = Date.now();
        if (this.latestStatus.stopped) {
            this.latestStatus.status.state = 0;
            this.latestStatus.status.description = 'Running';
            this.latestStatus.status.updated = now;
            this.latestStatus.initial = true;
            this.latestStatus.stopped = false;
        }
        if (this.heartbeatInterval >= 0) {
            const latest = this.latestStatus.status;
            const status: core.Status = {
                ...latest,
                timestamp: now,
                expires: now + (3 * this.heartbeatInterval)
            };
            const msg = makeStatusUpdate(this.repoId, status, this.metadata);
            this.publisher(msg);
        }
        this.running = true;
        if (this.heartbeatInterval > 0) {
            this.heartbeatCleanup = setInterval(() => {
                if (!this.running) {
                    return;
                }
                const now = Date.now();
                const latest = this.latestStatus.status;
                const status: core.Status = {
                    ...latest,
                    timestamp: now,
                    expires: now + (3 * this.heartbeatInterval)
                };
                const msg = makeStatusUpdate(this.repoId, status, this.metadata);
                this.publisher(msg);
            }, this.heartbeatInterval);
        }
    }

    stop() {
        log.info(`stopping repository for ${JSON.stringify(this.repoId)}`);
        this.running = false;
        if (this.heartbeatCleanup) {
            clearInterval(this.heartbeatCleanup);
        }
        const now = Date.now();
        this.latestStatus.stopped = true;
        this.status({
            state: -1,
            timestamp: now,
            updated: now,
            expires: now,
            description: 'Repository Stopped'
        });
    }

    add(definitions: core.Definitions) {
        for (const def of definitions) {
            const key = def.name;
            if (!(this.metrics.has(key) || !(this.filters === undefined || isAllowed(this.repoId, key, this.filters)))) {
                this.metrics.set(key, def);
            }
        }
    }

    publish(dataPoints: core.DataPoints) {
        if (this.running && dataPoints.length > 0) {
            for (const dp of dataPoints) {
                const metric = this.metrics.get(dp.name);
                if (metric) {
                    const msg: common.DataPointEvent = {
                        identity: this.repoId,
                        metric: metric,
                        datapoint: {...dp}
                    };
                    delete msg.datapoint['name'];
                    this.publisher(msg);
                }
            }
        }
    }

    status(status: core.Status) {
        const publisherStatus: LatestStatus = {...this.latestStatus};
        Object.assign(publisherStatus.status, status);
        publisherStatus.timestamp = status.timestamp;
        publisherStatus.expires = status.expires;

        const statusChanged = publisherStatus.status.state !== this.latestStatus.status.state;
        if (this.latestStatus.initial || statusChanged) {
            publisherStatus.status.updated = status.timestamp;
        }
        publisherStatus.initial = false;

        Object.assign(this.latestStatus, publisherStatus);

        if (this.running || publisherStatus.stopped) {
            const msg = makeStatusUpdate(this.repoId, status, this.metadata);
            this.publisher(msg);
        }
    }
}

export class BasicRepositoryFactory implements core.RepositoryFactory {
    constructor(
        private readonly config: IOGateway.BasicMetricsPublisherConfig | undefined,
        private readonly publisher: Publisher) {
    }

    repository(peerIdentity: IOGateway.Metrics.Identity, opts?: core.Options): core.Repository {
        const peerMetadata = opts?.metadata;
        const heartbeatInterval = this.config?.heartbeats ?? 0;
        return new BasicRepository(this.publisher, this.config?.filters, peerIdentity, heartbeatInterval, peerMetadata);
    }

    async shutdown() {
        await this.publisher.close();
    }

    on(listener: (event: common.MetricsEvent) => void) {
        this.publisher.on(listener);
    }
}


export interface Publisher {
    (msg: common.MetricsEvent): void
    on(listener: (event: common.MetricsEvent) => void): void
    close(): Promise<void>
}

export type WorkerConfig =  {
    preload?: string,
    worker?: { url: URL, parameters: {logLevel?: LogConfig['level']}, options?: unknown  }
    publishFn?: ((command: PublishCommand) => Promise<unknown>) | string
}


export interface WorkerCommandType {
    "start": {
        cfg: BasicPublisherConfig | undefined,
        publishFn: ((command: PublishCommand) => Promise<unknown>) | string
    }
    "update": IOGateway.Metrics.Update
    "stop": unknown
}

export type WorkerRequest<C extends keyof WorkerCommandType> = {
    id: number
    cmd: C,
    arg: WorkerCommandType[C]
}

export type BasicPublisherConfig = IOGateway.BasicMetricsPublisherConfig & {preload?: string, worker?: {url: URL, options?: unknown}};

export async function basicPublisher<C extends BasicPublisherConfig>(
    cfg: C | undefined,
    xform: (data: common.MetricsEvent[]) => Iterable<IOGateway.Metrics.Update>,
    publishFn: ((command: PublishCommand) => Promise<unknown>) | string
): Promise<Publisher> {
    log.info(`creating publisher with configuration\n${JSON.stringify(cfg, sanitizerOf('authentication.password'))}`);

    // add support for in process publishing
    const workerCfg = cfg?.worker;
    if (workerCfg === undefined) {
        throw new Error('in proc publishing is not supported. please provide worker configuration');
    }
    const cfgWithoutWorker = cfg ?
        Object.fromEntries(
            Object.entries(cfg)
                .filter(([key]) => key !== 'worker')
        ) : {};

    try {
        return await publishInWorker(publishFn as string, cfgWithoutWorker, xform, workerCfg);
    } catch (e) {
        log.error(`Failed to create basic publisher`, e);
        throw e;
    }
}

class WorkerPublisher {
    private readonly maxQueueSize: number;
    private readonly cfg: Omit<BasicPublisherConfig, 'worker'>;
    private readonly worker: Worker
    private lastId: number;
    private readonly processing: Map<number, {resolver: {resolve, reject}}>;
    private readonly promises: Map<number, Promise<void>>;
    private readonly listeners: ((event: common.MetricsEvent) => void)[];
    constructor(worker: Worker, cfg: Omit<BasicPublisherConfig, 'worker'>) {
        const {buffer_size: bufferSize} = cfg;
        this.maxQueueSize = bufferSize ?? 1000;
        this.cfg = cfg;
        this.lastId = 0;
        this.worker = worker;
        this.processing = new Map<number, { resolver: { resolve, reject } }>();
        this.promises = new Map<number, Promise<void>>();
        this.listeners = [];
    }

    async start(publishFn: string) {
        await new Promise<void>((resolve) => {
            this.worker.onerror = (event) => {
                log.error(`error from worker: ${event.message}`);
            };
            this.worker.onmessageerror = (event) => {
                log.error(`error receiving message: ${event.data}`);
            }
            this.worker.onmessage = (event) => {
                if (event.data.ready) {
                    resolve();
                    return;
                }
                if (event.data.id) {
                    const {id, result, error} = event.data;
                    const p = this.processing.get(id);
                    if (p) {
                        const {resolver} = p;
                        try {
                            if (error) {
                                resolver.reject(deserializeError(error));
                            } else {
                                resolver.resolve(result);
                            }
                        } finally {
                            this.processing.delete(id);
                        }
                    } else {
                        log.error(`unknown message id: ${id}`);
                    }
                } else if (event.data.log) {
                    const {level, time, name, message, data} = event.data.log as LogEvent;
                    logEvent({level, time: new Date(time), name, message, data: data.map(d => deserializeError(d))});
                } else if (event.data.event) {
                    this.listeners.forEach(l => l(event.data.event));
                }
            };
        });
        try {
            /*const context = */await this.enqueue('start', {cfg: this.cfg, publishFn})[1];
            log.info(`publisher worker [${publishFn}] started`);
        } catch (e) {
            log.error(`error starting publisher worker [${publishFn}]: ${e}`);
            this.worker.terminate();
            throw e;
        }
    }

    private enqueue<T, K extends keyof WorkerCommandType>(cmd: K, arg: WorkerCommandType[K]): [number, Promise<T>] {
        if (this.processing.size >= this.maxQueueSize) {
            log.warn(`processing queue is full. dropping cmd: ${JSON.stringify(cmd)}`);
        }
        const id = ++this.lastId;
        return [id, new Promise<T>((resolve, reject) => {
            this.processing.set(id, {resolver: {resolve, reject}});
            const request: WorkerRequest<K> = {id, cmd, arg};
            this.worker.postMessage(request);
        })];
    }

    next(u: IOGateway.Metrics.Update) {
        const [id, p] = this.enqueue<void, 'update'>('update', u);
        p.catch((e) => {
            log.warn(`update [${id}] error`, e);
        }).finally(() => {
            this.promises.delete(id);
        });
    }

    async stop(rejectProcessing = false, timeout = 1000) {
        log.info(`stopping publisher worker rejectProcessing: ${rejectProcessing}, timeout: ${timeout}, promises: ${this.promises.size}`);
        const rejectProcessingFn = (reason: 'now' | 'timeout') => {
            this.processing.forEach(({resolver}) => {
                resolver.reject(new Error(`reject on stop: ${reason}`));
            });
        };
        try {
            if (rejectProcessing) {
                rejectProcessingFn('now');
            }
            const pending = Promise.allSettled(this.promises);
            const stopPromise = this.enqueue('stop', undefined)[1];
            await new Promise<void>((resolve, reject) => {
                const timeoutId = setTimeout(() => {
                    rejectProcessingFn('timeout');
                    reject(new Error('timeout'));
                }, timeout);
                pending.then((outcomes) => {
                    clearTimeout(timeoutId);
                    for (const outcome of outcomes) {
                        if (outcome.status === 'rejected') {
                            log.error(`Pending future failed with ${outcome.reason} on stop`)
                        }
                    }
                    resolve();
                });
            });
            await stopPromise;
            if (this.promises.size > 0) {
                log.error(`uncleared promises: ${this.promises.size}`);
            }
            log.info(`publisher worker stopped`);
        } finally {
            this.worker.terminate();
        }
    }

    on(listener: (event: common.MetricsEvent) => void) {
        this.listeners.push(listener);
    }

}

async function publishInWorker(publishFn: string,
                               cfg: Omit<BasicPublisherConfig, 'worker'>,
                               xform: (data: common.MetricsEvent[]) => Iterable<IOGateway.Metrics.Update>,
                               workerCfg: {url: URL, parameters?: Record<string, string>, options?: unknown}): Promise<Publisher> {


    const worker = new WorkerPublisher(await newWorker(workerCfg), cfg);
    await worker.start(publishFn);


    const publisher: Publisher = (msg: common.MetricsEvent): void => {
        for (const u of xform([msg])) {
            worker.next(u);
        }
    };
    publisher.close = async (): Promise<void> => {
        await worker.stop();
    };
    publisher.on = (listener: (event: common.MetricsEvent) => void): void => {
        worker.on(listener);
    };

    return publisher;
}

export function basicRepositoryFactory(config: IOGateway.BasicMetricsPublisherConfig | undefined, publisher: Publisher): BasicRepositoryFactory {
    return new BasicRepositoryFactory(config, publisher);
}
